import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

import Body from './Body'
import { refreshFromServer } from '../services/refreshApp'

// #119: generic pull-to-refresh for every screen. Body is the single app-wide
// scroll container, so wrapping it here covers all routes. Pulling down from the
// very top past a threshold refreshes the app from the server (clears caches +
// SW, reloads) — the same action as the side-menu "refresh", now via a gesture.

export const PULL_THRESHOLD = 70 // px of real pull needed to trigger
export const MAX_PULL = 120 // cap on tracked pull distance

// Pure helpers (unit-tested):
export function computePullDistance(delta, max = MAX_PULL) {
  if (!(delta > 0)) return 0
  return Math.min(max, delta)
}

export function shouldRefresh(pullDistance, threshold = PULL_THRESHOLD) {
  return pullDistance >= threshold
}

// Visual travel is dampened (rubber-band feel) relative to the tracked pull.
export function indicatorOffset(pullDistance) {
  return Math.round(computePullDistance(pullDistance) * 0.5)
}

export default function PullToRefresh({ children, onRefresh }) {
  const containerRef = useRef(null)
  const startYRef = useRef(null)
  const pullingRef = useRef(false)
  const pullDistanceRef = useRef(0)
  const [pullDistance, setPullDistance] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  const setPull = useCallback((value) => {
    pullDistanceRef.current = value
    setPullDistance(value)
  }, [])

  const triggerRefresh = useCallback(async () => {
    setRefreshing(true)
    // A successful refresh reloads the page (this component unmounts). But if the
    // reload is cancelled — e.g. the browser's unsaved-changes prompt is
    // dismissed — the page stays mounted; without this the spinner would hang and
    // block further pulls. Reset shortly after so the user can retry.
    const safety = setTimeout(() => {
      setRefreshing(false)
      setPull(0)
    }, 2500)
    try {
      await (onRefresh ? onRefresh() : refreshFromServer())
    } catch (error) {
      console.warn('Pull-to-refresh failed:', error)
      clearTimeout(safety)
      setRefreshing(false)
      setPull(0)
    }
  }, [onRefresh, setPull])

  useEffect(() => {
    const el = containerRef.current
    if (!el) {
      return undefined
    }

    const onTouchStart = (event) => {
      if (refreshing || event.touches.length !== 1 || el.scrollTop > 0) {
        pullingRef.current = false
        return
      }
      startYRef.current = event.touches[0].clientY
      pullingRef.current = true
    }

    const onTouchMove = (event) => {
      if (!pullingRef.current || refreshing || startYRef.current == null) {
        return
      }
      const delta = event.touches[0].clientY - startYRef.current
      if (delta > 0 && el.scrollTop <= 0) {
        setPull(computePullDistance(delta))
        if (delta > 5) {
          event.preventDefault() // suppress native overscroll while pulling
        }
      } else {
        pullingRef.current = false
        setPull(0)
      }
    }

    const onTouchEnd = () => {
      if (!pullingRef.current || refreshing) {
        setPull(0)
        return
      }
      pullingRef.current = false
      if (shouldRefresh(pullDistanceRef.current)) {
        triggerRefresh()
      } else {
        setPull(0)
      }
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd, { passive: true })
    el.addEventListener('touchcancel', onTouchEnd, { passive: true })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [refreshing, triggerRefresh, setPull])

  const active = refreshing || pullDistance > 0
  const offset = refreshing ? 40 : indicatorOffset(pullDistance)
  const progress = Math.min(100, (pullDistance / PULL_THRESHOLD) * 100)

  return (
    <Body ref={containerRef} sx={{ position: 'relative' }}>
      <Box
        aria-hidden={!active}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: 5,
          transform: `translateY(${offset - 32}px)`,
          opacity: active ? 1 : 0,
          transition: pullingRef.current ? 'none' : 'transform 0.2s, opacity 0.2s',
        }}
      >
        <CircularProgress
          size={28}
          variant={refreshing || progress >= 100 ? 'indeterminate' : 'determinate'}
          value={progress}
        />
      </Box>
      {children}
    </Body>
  )
}
